import { baseApi } from '../../../core/baseApi';
import type {
  Package,
  Subscription,
  CreateSubscriptionBody,
  CreateSubscriptionByCodeBody,
  CancelSubscriptionArgs,
} from '../types';
import type { ApiResponse } from '../../../patterns/crud';

export const subscriptionsApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // Get available packages
    getPackages: builder.query<Package[], void>({
      query: () => '/billing/packages',
      transformResponse: (response: ApiResponse<Package[]>) => response.data,
      providesTags: ['Packages'],
    }),

    // Get organization's subscriptions
    getSubscriptions: builder.query<Subscription[], void>({
      query: () => '/billing/subscriptions',
      transformResponse: (response: ApiResponse<Subscription[]>) => response.data,
      providesTags: ['Subscriptions'],
    }),

    // Create a subscription
    createSubscription: builder.mutation<Subscription, CreateSubscriptionBody>({
      query: (body) => ({
        url: '/billing/subscriptions',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<Subscription>) => response.data,
      invalidatesTags: [{ type: 'Subscriptions', id: 'LIST' }],
    }),

    // Create a subscription by package code (for automated flows)
    createSubscriptionByCode: builder.mutation<Subscription | { message: string }, CreateSubscriptionByCodeBody>({
      query: (body) => ({
        url: '/billing/subscriptions/by-code',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<Subscription> | { success: boolean; message: string }) => {
        if ('data' in response) {
          return response.data;
        }
        return { message: response.message || 'Subscription created' };
      },
      invalidatesTags: [{ type: 'Subscriptions', id: 'LIST' }],
    }),

    // Cancel a subscription
    cancelSubscription: builder.mutation<Subscription, CancelSubscriptionArgs>({
      query: ({ id, ...body }) => ({
        url: `/billing/subscriptions/${id}`,
        method: 'DELETE',
        body,
      }),
      transformResponse: (response: ApiResponse<Subscription>) => response.data,
      invalidatesTags: [{ type: 'Subscriptions', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetPackagesQuery,
  useGetSubscriptionsQuery,
  useCreateSubscriptionMutation,
  useCreateSubscriptionByCodeMutation,
  useCancelSubscriptionMutation,
} = subscriptionsApi;
